/**
 * Settings → Payments. Connect the workshop's own gateway.
 *
 * Deliberately short: pick a provider, paste two fields, test, save. Saved
 * secrets come back masked with a Replace button — seeing `••••••••3f21` is
 * what tells an owner at a glance that the thing is actually secured.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  CreditCard, Loader2, Check, X, Copy, ShieldCheck, Unplug, Info, RefreshCw,
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls } from './shared';

const PROVIDERS = [
  {
    key: 'razorpay',
    name: 'Razorpay',
    blurb: 'Cards, UPI, netbanking and wallets. Invoices reconcile themselves.',
    idLabel: 'Key ID',
    idHint: 'Starts with rzp_test_ or rzp_live_',
    secretLabel: 'Key Secret',
  },
  {
    key: 'cashfree',
    name: 'Cashfree',
    blurb: 'Cards, UPI, netbanking and wallets. Invoices reconcile themselves.',
    idLabel: 'App ID',
    idHint: 'From Cashfree Dashboard → Developers → API Keys',
    secretLabel: 'Secret Key',
  },
  {
    key: 'upi',
    name: 'UPI ID only',
    blurb: 'No gateway account needed — just your VPA on the invoice QR.',
  },
];

const EMPTY = {
  provider: '', mode: 'test', key_id: '', key_secret: '', webhook_secret: '',
  upi_vpa: '', upi_name: '',
};

export const PaymentGatewayCard = () => {
  const { authHeader } = useAuth();
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [replacing, setReplacing] = useState(false);
  const [replacingWebhook, setReplacingWebhook] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/payments/gateway`, { headers: authHeader() });
      setSaved(data);
      setForm({
        ...EMPTY,
        provider: data.provider || '',
        mode: data.mode || 'test',
        key_id: data.key_id || '',
        upi_vpa: data.upi_vpa || '',
        upi_name: data.upi_name || '',
      });
      setReplacing(false);
    } catch (e) {
      setErr(formatErr(e));
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Switching provider starts a clean form, except when it is the provider
  // already saved — then the stored Key ID comes back so a mode change or a
  // secret replacement does not need it retyped.
  const pickProvider = (key) => {
    setMsg(''); setErr(''); setReplacing(false); setReplacingWebhook(false);
    setForm({
      ...EMPTY,
      provider: key,
      mode: saved?.provider === key ? (saved.mode || 'test') : 'test',
      key_id: saved?.provider === key ? (saved.key_id || '') : '',
      upi_vpa: saved?.provider === key ? (saved.upi_vpa || '') : '',
      upi_name: saved?.provider === key ? (saved.upi_name || '') : '',
    });
  };
  const chosen = PROVIDERS.find((p) => p.key === form.provider);
  const isGateway = form.provider === 'razorpay' || form.provider === 'cashfree';
  const hasStoredSecret = Boolean(saved?.key_secret_masked) && saved?.provider === form.provider;
  // "Test connection" and the Connected pill both describe the *stored*
  // gateway — neither means anything while the form shows something else.
  const inSyncWithSaved = saved?.provider === form.provider && saved?.mode === form.mode;

  const run = async (label, fn) => {
    setBusy(label); setMsg(''); setErr('');
    try { await fn(); } catch (e) { setErr(formatErr(e)); } finally { setBusy(''); }
  };

  const save = () => run('save', async () => {
    const payload = { ...form };
    if (hasStoredSecret && !replacing) payload.key_secret = '';
    const { data } = await axios.put(`${API}/payments/gateway`, payload, { headers: authHeader() });
    setSaved(data);
    setReplacing(false);
    setReplacingWebhook(false);
    setForm((s) => ({ ...s, key_secret: '', webhook_secret: '' }));
    setMsg(isGateway
      ? 'Saved. Press "Test connection" to confirm the keys work before a customer sees a payment link.'
      : 'Saved. Your UPI QR now appears on every unpaid invoice.');
  });

  const savePolicy = (percent) => run('policy', async () => {
    await axios.put(`${API}/payments/deposit-policy`, { percent },
      { headers: authHeader() });
    setSaved((s) => ({ ...s, deposit_percent_default: percent }));
    setMsg(percent === 0
      ? 'Approved estimates will not ask for a deposit.'
      : `Approved estimates will ask for a ${percent}% deposit.`);
  });

  const test = () => run('test', async () => {
    const { data } = await axios.post(`${API}/payments/gateway/test`, {}, { headers: authHeader() });
    setSaved(data.gateway);
    if (data.ok) setMsg(data.message || 'Connection verified.');
    else setErr(data.message || 'The gateway rejected these keys.');
  });

  const disconnect = () => {
    if (!window.confirm('Disconnect this gateway? Payment links already shared with customers will stop working.')) return;
    run('disconnect', async () => {
      await axios.delete(`${API}/payments/gateway`, { headers: authHeader() });
      await load();
      setMsg('Disconnected. Unpaid invoices fall back to your bank details.');
    });
  };

  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(saved.webhook_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked — the field is selectable anyway */ }
  };

  if (!saved) {
    return (
      <section className="bg-white border border-slate-200 rounded-xl p-5">
        <Loader2 className="animate-spin text-slate-400" size={16} />
      </section>
    );
  }

  return (
    <section id="payments" className="bg-white border border-slate-200 rounded-xl p-5 space-y-5 scroll-mt-20" data-testid="payments-section">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">Payments</h2>
        <span className="text-[10px] text-slate-400">Money settles to your own account</span>
      </div>

      <p className="text-xs text-slate-500 -mt-2">
        Connect your gateway and every invoice gets a Pay now link and QR — on the PDF,
        in the email and in the WhatsApp message. Driveline never holds the money and
        adds no fee.
      </p>

      {msg && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="payments-msg">{msg}</div>}
      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="payments-err">{err}</div>}

      {/* Provider choice — one active method at a time, on purpose. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {PROVIDERS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => pickProvider(p.key)}
            className={`text-left rounded-xl border p-3 transition-colors ${
              form.provider === p.key
                ? 'border-teal-500 bg-teal-50/60 ring-1 ring-teal-500/30'
                : 'border-slate-200 hover:border-slate-300'
            }`}
            data-testid={`provider-${p.key}`}
          >
            <div className="flex items-center gap-2">
              <CreditCard size={14} className={form.provider === p.key ? 'text-teal-600' : 'text-slate-400'} />
              <span className="text-sm font-semibold text-slate-900">{p.name}</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1 leading-snug">{p.blurb}</p>
          </button>
        ))}
      </div>

      {isGateway && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-slate-500">Mode</span>
            <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
              {['test', 'live'].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => set('mode', m)}
                  className={`px-3 py-1 text-xs font-semibold capitalize ${
                    form.mode === m ? 'bg-slate-900 text-white' : 'bg-white text-slate-600'
                  }`}
                  data-testid={`mode-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
            <span className="text-[11px] text-slate-400">
              Start in test, pay yourself with a test card, then switch to live.
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">{chosen.idLabel}</label>
              <input
                value={form.key_id}
                onChange={(e) => set('key_id', e.target.value.trim())}
                className={`${inputCls} font-mono text-xs`}
                placeholder={chosen.idHint}
                data-testid="gateway-key-id"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">{chosen.secretLabel}</label>
              {hasStoredSecret && !replacing ? (
                <div className="flex items-center gap-2">
                  <span className="flex-1 font-mono text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-600" data-testid="gateway-secret-masked">
                    {saved.key_secret_masked}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setReplacing(true); set('key_secret', ''); }}
                    className="text-xs font-semibold text-teal-600 hover:text-teal-700"
                    data-testid="replace-secret-btn"
                  >
                    Replace
                  </button>
                </div>
              ) : (
                <input
                  type="password"
                  value={form.key_secret}
                  onChange={(e) => set('key_secret', e.target.value.trim())}
                  className={`${inputCls} font-mono text-xs`}
                  placeholder="Paste the secret — it is encrypted and never shown again"
                  data-testid="gateway-key-secret"
                />
              )}
            </div>
            {form.provider === 'razorpay' && (
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Webhook secret</label>
                {saved.webhook_secret_masked && !replacingWebhook ? (
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-600">
                      {saved.webhook_secret_masked}
                    </span>
                    <button
                      type="button"
                      onClick={() => setReplacingWebhook(true)}
                      className="text-xs font-semibold text-teal-600 hover:text-teal-700"
                      data-testid="replace-webhook-secret-btn"
                    >
                      Replace
                    </button>
                  </div>
                ) : (
                  <input
                    type="password"
                    value={form.webhook_secret}
                    onChange={(e) => set('webhook_secret', e.target.value.trim())}
                    className={`${inputCls} font-mono text-xs`}
                    placeholder="The secret you set on the webhook in Razorpay"
                    data-testid="gateway-webhook-secret"
                  />
                )}
                <div className="text-[10px] text-slate-400 mt-1">
                  Razorpay signs webhooks with this. Without it we fall back to checking
                  payment status with Razorpay when the invoice is next opened.
                </div>
              </div>
            )}
          </div>

          {saved.webhook_url && saved.provider === form.provider && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="text-xs font-semibold text-slate-700 mb-1">Your webhook URL</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-[11px] text-slate-600 break-all" data-testid="webhook-url">{saved.webhook_url}</code>
                <button type="button" onClick={copyWebhook} className="shrink-0 text-slate-500 hover:text-teal-600" data-testid="copy-webhook-btn">
                  {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                </button>
              </div>
              <div className="text-[10px] text-slate-500 mt-1.5">
                Paste this into {chosen.name} → Webhooks and subscribe to payment link events.
                It is unique to your workshop.
              </div>
            </div>
          )}
        </div>
      )}

      {form.provider === 'upi' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">UPI ID (VPA)</label>
              <input
                value={form.upi_vpa}
                onChange={(e) => set('upi_vpa', e.target.value.trim())}
                className={`${inputCls} font-mono text-xs`}
                placeholder="e.g. workshop@okhdfcbank"
                data-testid="upi-vpa"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Account name</label>
              <input
                value={form.upi_name}
                onChange={(e) => set('upi_name', e.target.value)}
                className={inputCls}
                placeholder="Shown in the customer's UPI app"
                data-testid="upi-name"
              />
            </div>
          </div>
          <div className="flex gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5" data-testid="upi-limit-note">
            <Info size={15} className="shrink-0 mt-0.5" />
            <div>
              <b>A UPI QR cannot tell us when money arrives.</b> The QR and your UPI ID
              will appear on the invoice, email and WhatsApp message, but the invoice
              will not mark itself paid — you confirm it with the UTR reference using
              "Record payment". Connecting Razorpay or Cashfree gets you automatic
              reconciliation and a receipt sent for you.
            </div>
          </div>
        </div>
      )}

      {form.provider && saved.can_edit === false && (
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="payments-owner-only">
          Only the workshop owner can connect or change a payment gateway.
        </div>
      )}

      {form.provider && saved.can_edit !== false && (
        <div className="flex flex-wrap items-center gap-2 pt-1">          <button
            onClick={save}
            disabled={Boolean(busy)}
            className="inline-flex items-center gap-1.5 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white text-sm font-semibold px-4 py-2 rounded-lg"
            data-testid="save-gateway-btn"
          >
            {busy === 'save' ? <Loader2 className="animate-spin" size={14} /> : <ShieldCheck size={14} />}
            Save
          </button>
          {isGateway && (
            <button
              onClick={test}
              disabled={Boolean(busy) || !saved.key_id || !inSyncWithSaved}
              title={inSyncWithSaved ? '' : 'Save your changes first — this tests the stored keys.'}
              className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-slate-400 disabled:opacity-50 text-slate-700 text-sm font-semibold px-4 py-2 rounded-lg"
              data-testid="test-gateway-btn"
            >
              {busy === 'test' ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
              Test connection
            </button>
          )}
          {saved.provider && (
            <button
              onClick={disconnect}
              disabled={Boolean(busy)}
              className="inline-flex items-center gap-1.5 text-rose-600 hover:text-rose-700 text-sm font-semibold px-3 py-2 rounded-lg"
              data-testid="disconnect-gateway-btn"
            >
              <Unplug size={14} />
              Disconnect
            </button>
          )}
          {saved.connected && inSyncWithSaved && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1" data-testid="gateway-connected-pill">
              <Check size={12} /> Connected
            </span>
          )}
          {saved.last_test_ok === false && inSyncWithSaved && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2.5 py-1">
              <X size={12} /> Last test failed
            </span>
          )}
        </div>
      )}
      {saved.connected && saved.auto_reconcile && (
        <div className="border-t border-slate-100 pt-4" data-testid="deposit-policy">
          <div className="text-sm font-semibold text-slate-900">Deposit on approval</div>
          <p className="text-xs text-slate-500 mt-0.5 mb-2.5 max-w-xl">
            When an estimate is approved — by your team or by the customer on their
            own link — ask for this much up front and send them the payment link.
            It is adjusted against the final invoice.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {[0, 25, 50, 70].map((p) => (
              <button
                key={p}
                type="button"
                disabled={busy === 'policy'}
                onClick={() => savePolicy(p)}
                className={`text-xs font-semibold rounded-full border px-3 py-1.5 ${
                  Number(saved.deposit_percent_default) === p
                    ? 'border-teal-500 bg-teal-50 text-teal-700'
                    : 'border-slate-300 text-slate-600 hover:border-slate-400'
                }`}
                data-testid={`deposit-policy-${p}`}
              >
                {p === 0 ? "Don't ask" : `${p}%`}
              </button>
            ))}
            {busy === 'policy' && <Loader2 className="animate-spin text-slate-400" size={14} />}
          </div>
        </div>
      )}
    </section>
  );
};

export default PaymentGatewayCard;
